import { useEffect, useState } from 'react'
import confetti from 'canvas-confetti'
import AnimationLayout from './components/AnimationLayout'

interface Badge {
  name: string
  emoji: string
  description: string
  points_awarded: number
}

interface Props {
  animation: { id: number; name: string; description: string }
  badge: Badge
  all_animations: { id: number; name: string }[]
}

export default function Animation20({ animation, badge, all_animations }: Props) {
  const [showTrophy, setShowTrophy] = useState(false)
  const [showBadge, setShowBadge] = useState(false)

  useEffect(() => {
    setTimeout(() => setShowTrophy(true), 300)

    setTimeout(() => {
      setShowBadge(true)
      confetti({
        particleCount: 200,
        spread: 100,
        origin: { y: 0.5 },
        colors: ['#0F352D', '#1d5c4d', '#FFF198', '#feee80', '#fcf5cc']
      })
    }, 1200)
  }, [])

  return (
    <AnimationLayout animation={animation} allAnimations={all_animations}>
      <style>{`
        @keyframes fireFlicker {
          0%, 100% {
            transform: scaleY(1) scaleX(1);
            opacity: 1;
          }
          25% {
            transform: scaleY(1.1) scaleX(0.95);
          }
          50% {
            transform: scaleY(0.9) scaleX(1.05);
            opacity: 0.9;
          }
          75% {
            transform: scaleY(1.05) scaleX(0.98);
          }
        }
        @keyframes trophyRise {
          0% {
            transform: translateY(100px) scale(0);
            opacity: 0;
          }
          60% {
            transform: translateY(-20px) scale(1.1);
          }
          100% {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
        }
        @keyframes glow {
          0%, 100% {
            box-shadow: 0 0 30px rgba(255, 241, 152, 0.5);
          }
          50% {
            box-shadow: 0 0 60px rgba(255, 241, 152, 0.8);
          }
        }
        .fire-flicker {
          animation: fireFlicker 0.3s ease-in-out infinite;
        }
        .trophy-rise {
          animation: trophyRise 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        .glow {
          animation: glow 2s ease-in-out infinite;
        }
      `}</style>

      <div className="flex flex-col items-center justify-center min-h-[50vh] sm:min-h-[60vh] relative">
        {/* Flammes de fond */}
        {showTrophy && (
          <div className="absolute inset-x-0 bottom-20 flex items-end justify-center pointer-events-none">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="fire-flicker mx-1"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div
                  className="w-12 bg-gradient-to-t from-tessan-yellow via-tessan-cream to-transparent rounded-full blur-sm"
                  style={{ height: `${80 + Math.random() * 40}px`, opacity: 0.6 }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Trophée / Badge */}
        <div className={`relative z-10 ${showTrophy ? 'trophy-rise' : 'opacity-0'}`}>
          {!showBadge ? (
            <div className="text-6xl sm:text-8xl">🏆</div>
          ) : (
            <div className="glow w-48 h-48 bg-tessan-cream rounded-2xl flex items-center justify-center shadow-2xl">
              <span className="text-7xl sm:text-9xl">{badge.emoji}</span>
            </div>
          )}
        </div>

        {/* Texte */}
        <div className={`mt-8 text-center transition-all duration-500 ${showBadge ? 'opacity-100' : 'opacity-0'}`}>
          <div className="text-3xl sm:text-5xl font-black text-tessan-yellow mb-2">FÉLICITATIONS !</div>
          <div className="text-3xl font-bold text-tessan-cream">{badge.name}</div>
          <div className="text-xl text-tessan-yellow mt-2">+{badge.points_awarded} points</div>
          <div className="text-tessan-cream/70 mt-4 max-w-[280px] sm:max-w-xs">{badge.description}</div>
        </div>
      </div>
    </AnimationLayout>
  )
}
